/**
 * Sidebar content: logo area, country identity block, and navigation items.
 * Used by both the mobile temporary drawer and the desktop permanent sidebar.
 *
 * Navigation sections are driven by the selected functionality (from appStore)
 * combined with role-based filtering. Admin sections render at top for admins.
 * Static sections (Company, Community, Help) are pinned to the sidebar bottom
 * with a visual divider, separate from the scrollable functionality nav.
 */

import { type ReactElement, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  List,
  Typography,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  AccountTree as AccountTreeIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Article as ArticleIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  Calculate as CalculateIcon,
  Category as CategoryIcon,
  Chat as ChatIcon,
  ChevronLeft as ChevronLeftIcon,
  CompareArrows as CompareArrowsIcon,
  Dashboard as DashboardIcon,
  Flag as FlagIcon,
  Schedule as ScheduleIcon,
  DataObject as DataObjectIcon,
  HistoryEdu as HistoryEduIcon,
  Menu as MenuIcon,
  MoneyOff as MoneyOffIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  Receipt as ReceiptIcon,
  Security as SecurityIcon,
  ShowChart as ShowChartIcon,
  Summarize as SummarizeIcon,
  Gavel as GavelIcon,
  TrendingDown as TrendingDownIcon,
  GridOn as GridOnIcon,
  SwapVert as SwapVertIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  FactCheck as FactCheckIcon,
  Shield as ShieldIcon,
  TableChart as TableChartIcon,
  MenuBook as MenuBookIcon,
  PlayCircleOutline as TourIcon,
  Percent as PercentIcon,
  PieChart as PieChartIcon,
  MonitorHeart as MonitorHeartIcon,
  Tune as TuneIcon,
  PlayArrow as PlayArrowIcon,
  Speed as SpeedIcon,
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import Visibility from '@mui/icons-material/Visibility';
import { useQuery } from '@tanstack/react-query';
import { ifrs17Policies } from '../../api/endpoints';
import { useAuthStore } from '../../stores/authStore';
import { useAppStore } from '../../stores/appStore';
import { COLORS, FUNCTIONALITY_NAV, SIDEBAR_WIDTH } from './layoutConstants';
import type { NavItem, NavSection } from './layoutConstants';
import { SidebarNavItem, SectionHeader } from './SidebarNavItem';

// ─── Icon lookup: maps string keys from FUNCTIONALITY_NAV to MUI components ──

const ICON_MAP: Record<string, ReactElement> = {
  AccountBalance: <AccountBalanceIcon />,
  AccountBalanceWallet: <AccountBalanceWalletIcon />,
  AccountTree: <AccountTreeIcon />,
  Article: <ArticleIcon />,
  AttachMoney: <AttachMoneyIcon />,
  Calculate: <CalculateIcon />,
  Category: <CategoryIcon />,
  CheckCircleOutline: <CheckCircleOutlineIcon />,
  CompareArrows: <CompareArrowsIcon />,
  Dashboard: <DashboardIcon />,
  DataObject: <DataObjectIcon />,
  FactCheck: <FactCheckIcon />,
  GridOn: <GridOnIcon />,
  Gavel: <GavelIcon />,
  Link: <LinkIcon />,
  HistoryEdu: <HistoryEduIcon />,
  MoneyOff: <MoneyOffIcon />,
  Percent: <PercentIcon />,
  PieChart: <PieChartIcon />,
  Receipt: <ReceiptIcon />,
  Security: <SecurityIcon />,
  Shield: <ShieldIcon />,
  ShowChart: <ShowChartIcon />,
  Summarize: <SummarizeIcon />,
  SwapVert: <SwapVertIcon />,
  TableChart: <TableChartIcon />,
  TrendingDown: <TrendingDownIcon />,
  Visibility: <Visibility />,
  MonitorHeart: <MonitorHeartIcon />,
  PlayArrow: <PlayArrowIcon />,
  Speed: <SpeedIcon />,
  MenuBook: <MenuBookIcon />,
  CloudUpload: <CloudUploadIcon />,
};

function getIcon(name: string): ReactElement {
  return ICON_MAP[name] ?? <DashboardIcon />;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface SidebarContentProps {
  sidebarExpanded: boolean;
  isMobile: boolean;
  currentWidth: number;
  onDrawerToggle: () => void;
  onMobileNavClick: () => void;
}

export default function SidebarContent({
  sidebarExpanded,
  isMobile,
  currentWidth,
  onDrawerToggle,
  onMobileNavClick,
}: SidebarContentProps) {
  const user = useAuthStore((s) => s.user);
  const selectedFunctionality = useAppStore((s) => s.selectedFunctionality);

  // ── 7-tier RBAC role derivation ──────────────────────────────────────
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isCountryAdmin = user?.role === 'admin';
  const isRegulator = user?.role === 'regulator';
  const hasCompany =
    user?.role === 'company_user' ||
    user?.role === 'consultant_user' ||
    user?.role === 'auditor' ||
    user?.role === 'executive';

  // ── IFRS 4 Balance Sheet visibility (conditioned on policy toggle) ──
  const selectedYear = useAppStore((s) => s.selectedYear);
  const { data: allPolicies } = useQuery({
    queryKey: ['ifrs17Policies'],
    queryFn: () => ifrs17Policies.getAll(),
    staleTime: 10 * 60 * 1000,
    enabled: hasCompany,
  });
  const currentPolicy = allPolicies?.find((p) => p.year === selectedYear);
  const ifrs4Enabled = currentPolicy?.ifrs4_balance_sheet_enabled ?? false;

  // ── Collapse state per section label (persists across functionality switches) ─
  const [collapseStates, setCollapseStates] = useState<Record<string, boolean>>({});
  const BOTTOM_SECTIONS = new Set(['Company', 'Community', 'Help']);
  const isSectionOpen = (label: string) =>
    collapseStates[label] ?? (BOTTOM_SECTIONS.has(label) ? false : true);
  const toggleSection = (label: string) => {
    const defaultOpen = BOTTOM_SECTIONS.has(label) ? false : true;
    setCollapseStates((prev) => ({ ...prev, [label]: !(prev[label] ?? defaultOpen) }));
  };

  // ── Resolve functionality nav sections ──
  const sections: NavSection[] =
    FUNCTIONALITY_NAV[selectedFunctionality] ?? FUNCTIONALITY_NAV['audit_workbook'];

  const userRole = user?.role ?? '';

  /** Filter items by role and IFRS 4 toggle. */
  const filterItems = (items: NavItem[]): NavItem[] =>
    items.filter((item) => {
      if (item.roles && !item.roles.includes(userRole)) return false;
      if (item.ifrs4Only && !ifrs4Enabled) return false;
      return true;
    });

  return (
    <Box
      sx={{
        width: isMobile ? SIDEBAR_WIDTH : currentWidth,
        backgroundColor: COLORS.sidebarBg,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        transition: isMobile ? 'none' : 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Logo area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarExpanded ? 'space-between' : 'center',
          py: '28px',
          px: sidebarExpanded ? 2.5 : 1,
          borderBottom: `1px solid ${COLORS.sidebarBorder}`,
          flexShrink: 0,
        }}
      >
        {sidebarExpanded ? (
          <Box
            component="img"
            src="/logo-light.png"
            alt="Terra17"
            sx={{ height: 40, objectFit: 'contain' }}
          />
        ) : (
          <Box
            component="img"
            src="/favicon-light.svg"
            alt="Terra17"
            sx={{ height: 32, objectFit: 'contain' }}
          />
        )}
        {sidebarExpanded && (
          <IconButton
            onClick={onDrawerToggle}
            size="small"
            sx={{
              color: COLORS.navText,
              '&:hover': { color: COLORS.navTextHover, backgroundColor: COLORS.sidebarHoverBg },
            }}
            aria-label={isMobile ? 'close menu' : 'collapse sidebar'}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Expand toggle when collapsed (desktop only) */}
      {!isMobile && !sidebarExpanded && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1.5,
            pb: 0.5,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={onDrawerToggle}
            size="small"
            sx={{
              color: COLORS.navText,
              '&:hover': {
                color: COLORS.navTextHover,
                backgroundColor: COLORS.sidebarHoverBg,
              },
            }}
            aria-label="expand sidebar"
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* ── Country identity block (sidebar, admin only) ──── */}
      {isCountryAdmin && user?.country_name && sidebarExpanded && (
        <Box
          sx={{
            mx: 1.5,
            mt: 1.5,
            mb: 0.5,
            px: 2,
            py: 1.5,
            borderRadius: '10px',
            background:
              'linear-gradient(135deg, rgba(107, 63, 160, 0.15) 0%, rgba(70, 95, 255, 0.10) 100%)',
            border: '1px solid rgba(107, 63, 160, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          {user.country_flag_emoji && (
            <Typography sx={{ fontSize: 32, lineHeight: 1 }}>
              {user.country_flag_emoji}
            </Typography>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: '#FFFFFF',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user.country_name}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: COLORS.sectionHeader,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Admin
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Primary scrollable nav area ─────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: 1,
          pb: 1,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.sidebarBorder,
            borderRadius: 2,
          },
        }}
      >
        <List component="nav" disablePadding>
          {/* ── Admin section (admin-only, always visible) ─────────── */}
          {isAdmin && (
            <>
              <SectionHeader
                label="Admin"
                collapsible
                open={isSectionOpen('Admin')}
                onToggle={() => toggleSection('Admin')}
                sidebarExpanded={sidebarExpanded}
              />
              <Collapse in={sidebarExpanded ? isSectionOpen('Admin') : true} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <SidebarNavItem
                    to="/admin"
                    label="Dashboard"
                    icon={<AdminPanelSettingsIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                  {isSuperAdmin && (
                    <SidebarNavItem
                      to="/admin/countries"
                      label="Countries"
                      icon={<PublicIcon />}
                      sidebarExpanded={sidebarExpanded}
                      onNavigate={onMobileNavClick}
                    />
                  )}
                  <SidebarNavItem
                    to="/admin/country-business-classes"
                    label="Business Classes"
                    icon={<CategoryIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                  <SidebarNavItem
                    to="/admin/companies"
                    label="Companies"
                    icon={<BusinessIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                  <SidebarNavItem
                    to="/admin/users"
                    label="Users"
                    icon={<PeopleIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                </List>
              </Collapse>

              {isCountryAdmin && (
                <>
                  <SectionHeader
                    label="Country Data"
                    collapsible
                    open={isSectionOpen('Country Data')}
                    onToggle={() => toggleSection('Country Data')}
                    sidebarExpanded={sidebarExpanded}
                  />
                  <Collapse in={sidebarExpanded ? isSectionOpen('Country Data') : true} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <SidebarNavItem
                        to="/admin/yield-curves"
                        label="Yield Curves"
                        icon={<ShowChartIcon />}
                        sidebarExpanded={sidebarExpanded}
                        onNavigate={onMobileNavClick}
                      />
                      <SidebarNavItem
                        to="/admin/mortality-tables"
                        label="Mortality Tables"
                        icon={<GridOnIcon />}
                        sidebarExpanded={sidebarExpanded}
                        onNavigate={onMobileNavClick}
                      />
                      <SidebarNavItem
                        to="/admin/commission-signatures"
                        label="Commission Signatures"
                        icon={<PercentIcon />}
                        sidebarExpanded={sidebarExpanded}
                        onNavigate={onMobileNavClick}
                      />
                    </List>
                  </Collapse>
                </>
              )}
            </>
          )}

          {/* ── Back Office section (admin-only) ────────────────────── */}
          {isAdmin && (
            <>
              <SectionHeader
                label="Back Office"
                collapsible
                open={isSectionOpen('Back Office')}
                onToggle={() => toggleSection('Back Office')}
                sidebarExpanded={sidebarExpanded}
              />
              <Collapse in={sidebarExpanded ? isSectionOpen('Back Office') : true} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <SidebarNavItem
                    to="/admin/feature-flags"
                    label="Feature Flags"
                    icon={<FlagIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                  <SidebarNavItem
                    to="/admin/system-limits"
                    label="System Limits"
                    icon={<TuneIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                  <SidebarNavItem
                    to="/admin/scheduled-reports"
                    label="Scheduled Reports"
                    icon={<ScheduleIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                  <SidebarNavItem
                    to="/admin/job-dashboard"
                    label="Job Dashboard"
                    icon={<MonitorHeartIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                </List>
              </Collapse>
            </>
          )}

          {/* ── Regulator: minimal static nav ──────────────────────── */}
          {isRegulator && (
            <>
              <SectionHeader label="Dashboard" sidebarExpanded={sidebarExpanded} />
              <SidebarNavItem
                to="/dashboard"
                label="Dashboard"
                icon={<DashboardIcon />}
                sidebarExpanded={sidebarExpanded}
                onNavigate={onMobileNavClick}
              />
            </>
          )}

          {/* ── Functionality-driven sections ──────────────────────── */}
          {!isAdmin &&
            !isRegulator &&
            sections.map((section) => {
              const visibleItems = filterItems(section.items);
              if (visibleItems.length === 0) return null;

              const open = isSectionOpen(section.label);
              return (
                <Box key={section.label}>
                  <SectionHeader
                    label={section.label}
                    collapsible
                    open={open}
                    onToggle={() => toggleSection(section.label)}
                    sidebarExpanded={sidebarExpanded}
                  />
                  <Collapse
                    in={sidebarExpanded ? open : true}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {visibleItems.map((item) => (
                        <SidebarNavItem
                          key={item.to + item.label}
                          to={item.to}
                          label={item.label}
                          icon={getIcon(item.icon)}
                          indent={section.collapsible}
                          sidebarExpanded={sidebarExpanded}
                          onNavigate={onMobileNavClick}
                        />
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })}
        </List>
      </Box>

      {/* ── Pinned bottom: Company / Community / Help ──────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${COLORS.sidebarBorder}`,
          background: 'linear-gradient(180deg, rgba(28, 36, 52, 0) 0%, rgba(16, 20, 30, 0.4) 100%)',
          pt: 0.5,
          pb: 1.5,
          overflowY: 'auto',
          maxHeight: '40%',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.sidebarBorder,
            borderRadius: 2,
          },
        }}
      >
        <List component="nav" disablePadding>
          {/* ── Company (all non-admin authenticated users) ─────────── */}
          {(hasCompany || isRegulator) && (
            <>
              <SectionHeader
                label="Company"
                collapsible
                open={isSectionOpen('Company')}
                onToggle={() => toggleSection('Company')}
                sidebarExpanded={sidebarExpanded}
              />
              <Collapse in={sidebarExpanded ? isSectionOpen('Company') : true} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <SidebarNavItem
                    to="/company-info"
                    label="Company Info"
                    icon={<BusinessIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                  {!isRegulator && (
                    <>
                      <SidebarNavItem
                        to="/business-classes"
                        label="Business Classes"
                        icon={<CategoryIcon />}
                        sidebarExpanded={sidebarExpanded}
                        onNavigate={onMobileNavClick}
                      />
                      <SidebarNavItem
                        to="/contract-groups"
                        label="Contract Groups"
                        icon={<AccountTreeIcon />}
                        sidebarExpanded={sidebarExpanded}
                        onNavigate={onMobileNavClick}
                      />
                      <SidebarNavItem
                        to="/ifrs17-policies"
                        label="IFRS 17 Policies"
                        icon={<GavelIcon />}
                        sidebarExpanded={sidebarExpanded}
                        onNavigate={onMobileNavClick}
                      />
                    </>
                  )}
                </List>
              </Collapse>
            </>
          )}

          {/* ── Community (all authenticated users) ────────────────── */}
          <SectionHeader
            label="Community"
            collapsible
            open={isSectionOpen('Community')}
            onToggle={() => toggleSection('Community')}
            sidebarExpanded={sidebarExpanded}
          />
          <Collapse in={sidebarExpanded ? isSectionOpen('Community') : true} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <SidebarNavItem
                to="/profile"
                label="My Profile"
                icon={<PersonIcon />}
                sidebarExpanded={sidebarExpanded}
                onNavigate={onMobileNavClick}
              />
              <SidebarNavItem
                to="/directory"
                label="Directory"
                icon={<PeopleIcon />}
                sidebarExpanded={sidebarExpanded}
                onNavigate={onMobileNavClick}
              />
              <SidebarNavItem
                to="/messages"
                label="Messages"
                icon={<ChatIcon />}
                sidebarExpanded={sidebarExpanded}
                onNavigate={onMobileNavClick}
              />
            </List>
          </Collapse>

          {/* ── Help & Reference (non-admin users) ─────────────────── */}
          {!isAdmin && (
            <>
              <SectionHeader
                label="Help"
                collapsible
                open={isSectionOpen('Help')}
                onToggle={() => toggleSection('Help')}
                sidebarExpanded={sidebarExpanded}
              />
              <Collapse in={sidebarExpanded ? isSectionOpen('Help') : true} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {(userRole === 'consultant_user' || userRole === 'super_admin') && (
                    <SidebarNavItem
                      to="/act-engine-manual"
                      label="Engine Manual"
                      icon={<MenuBookIcon />}
                      sidebarExpanded={sidebarExpanded}
                      onNavigate={onMobileNavClick}
                    />
                  )}
                  <SidebarNavItem
                    to="/glossary"
                    label="IFRS 17 Glossary"
                    icon={<MenuBookIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                  <Box
                    onClick={() => {
                      localStorage.removeItem('ifrs17_walkthrough_completed');
                      window.location.reload();
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1,
                      mx: 1,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                    }}
                  >
                    <TourIcon sx={{ color: COLORS.navText, fontSize: 20 }} />
                    {sidebarExpanded && (
                      <Typography sx={{ fontSize: 13, color: COLORS.navText }}>
                        Restart Walkthrough
                      </Typography>
                    )}
                  </Box>
                </List>
              </Collapse>
            </>
          )}
        </List>
      </Box>
    </Box>
  );
}
